import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, apiError } from "../lib/api";
import { useAdminAuth } from "../context/AdminAuth";
import { useRefetchOnFocus } from "../hooks/useRefetchOnFocus";
import { ArrowLeft, Plus, Pencil, Trash2, ChevronUp, ChevronDown, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function AdminServices() {
  const nav = useNavigate();
  const { admin } = useAdminAuth();
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [catDlg, setCatDlg] = useState(null); // {mode,item}
  const [svcDlg, setSvcDlg] = useState(null); // {mode,item,category_id}
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/services");
      setCategories([...r.data.categories].sort((a, b) => a.order - b.order));
      setServices(r.data.services);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => {
    if (admin === false) nav("/admin");
    if (admin) load();
  }, [admin, load, nav]);
  useRefetchOnFocus(load);

  const openCat = (mode, item) => { setCatDlg({ mode, item }); setName(item?.name || ""); setActive(item ? item.active : true); };
  const openSvc = (mode, category_id, item) => { setSvcDlg({ mode, item, category_id }); setName(item?.name || ""); setActive(item ? item.active : true); };

  const saveCat = async () => {
    if (!name.trim()) { toast.error("Name erforderlich"); return; }
    setBusy(true);
    try {
      if (catDlg.mode === "new") await adminApi.post("/admin/service-categories", { name, active, order: categories.length });
      else await adminApi.put(`/admin/service-categories/${catDlg.item.id}`, { name, active, order: catDlg.item.order });
      toast.success("Gespeichert"); setCatDlg(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveSvc = async () => {
    if (!name.trim()) { toast.error("Name erforderlich"); return; }
    setBusy(true);
    try {
      const inCat = services.filter((s) => s.category_id === svcDlg.category_id);
      if (svcDlg.mode === "new") await adminApi.post("/admin/services", { category_id: svcDlg.category_id, name, active, order: inCat.length });
      else await adminApi.put(`/admin/services/${svcDlg.item.id}`, { category_id: svcDlg.category_id, name, active, order: svcDlg.item.order });
      toast.success("Gespeichert"); setSvcDlg(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const delCat = async (id) => { try { await adminApi.delete(`/admin/service-categories/${id}`); toast.success("Kategorie gelöscht"); load(); } catch (e) { toast.error(apiError(e)); } };
  const delSvc = async (id) => { try { await adminApi.delete(`/admin/services/${id}`); toast.success("Dienstleistung gelöscht"); load(); } catch (e) { toast.error(apiError(e)); } };

  const toggleSvc = async (s) => {
    const next = !s.active;
    setServices((prev) => prev.map((x) => x.id === s.id ? { ...x, active: next } : x));
    try {
      await adminApi.put(`/admin/services/${s.id}`, { category_id: s.category_id, name: s.name, active: next, order: s.order });
    } catch (e) {
      toast.error(apiError(e));
      setServices((prev) => prev.map((x) => x.id === s.id ? { ...x, active: s.active } : x));
    }
  };

  const moveCat = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= categories.length) return;
    const a = categories[idx], b = categories[j];
    try {
      await Promise.all([
        adminApi.put(`/admin/service-categories/${a.id}`, { name: a.name, active: a.active, order: b.order }),
        adminApi.put(`/admin/service-categories/${b.id}`, { name: b.name, active: b.active, order: a.order }),
      ]);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const moveSvc = async (list, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[idx], b = list[j];
    try {
      await Promise.all([
        adminApi.put(`/admin/services/${a.id}`, { category_id: a.category_id, name: a.name, active: a.active, order: b.order }),
        adminApi.put(`/admin/services/${b.id}`, { category_id: b.category_id, name: b.name, active: b.active, order: a.order }),
      ]);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#4A1D24] text-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <button data-testid="services-back-button" onClick={() => nav("/admin")} className="p-2 rounded-lg hover:bg-white/10"><ArrowLeft className="h-5 w-5" /></button>
          <div className="flex items-center gap-2"><Scissors className="h-5 w-5 text-[#C59B6C]" /><h1 className="font-serif text-2xl">Dienstleistungen verwalten</h1></div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <div className="flex justify-end">
          <Button data-testid="add-category-button" onClick={() => openCat("new")} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white"><Plus className="h-4 w-4 mr-1" /> Kategorie</Button>
        </div>

        {categories.map((cat, ci) => {
          const svcs = services.filter((s) => s.category_id === cat.id).sort((a, b) => a.order - b.order);
          return (
            <div key={cat.id} data-testid={`category-block-${cat.id}`} className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm">
              <div className="p-4 flex items-center justify-between border-b border-stone-100">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button data-testid={`cat-up-${cat.id}`} onClick={() => moveCat(ci, -1)} className="text-stone-300 hover:text-stone-600"><ChevronUp className="h-4 w-4" /></button>
                    <button data-testid={`cat-down-${cat.id}`} onClick={() => moveCat(ci, 1)} className="text-stone-300 hover:text-stone-600"><ChevronDown className="h-4 w-4" /></button>
                  </div>
                  <h2 className="font-serif text-xl text-stone-900">{cat.name}</h2>
                  {!cat.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 border border-stone-200">inaktiv</span>}
                </div>
                <div className="flex gap-1">
                  <Button data-testid={`add-service-${cat.id}`} size="sm" variant="outline" onClick={() => openSvc("new", cat.id)}><Plus className="h-4 w-4 mr-1" /> Dienstleistung</Button>
                  <button data-testid={`edit-category-${cat.id}`} onClick={() => openCat("edit", cat)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-category-${cat.id}`} onClick={() => delCat(cat.id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="divide-y divide-stone-100">
                {svcs.length === 0 && <p className="p-4 text-xs text-stone-400">Keine Dienstleistungen.</p>}
                {svcs.map((s, si) => (
                  <div key={s.id} data-testid={`service-row-${s.id}`} className="px-4 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex flex-col">
                        <button onClick={() => moveSvc(svcs, si, -1)} className="text-stone-300 hover:text-stone-600"><ChevronUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => moveSvc(svcs, si, 1)} className="text-stone-300 hover:text-stone-600"><ChevronDown className="h-3.5 w-3.5" /></button>
                      </div>
                      <span className={`text-sm truncate ${s.active ? "text-stone-800" : "text-stone-400 line-through"}`}>{s.name}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Switch data-testid={`toggle-service-${s.id}`} checked={s.active} onCheckedChange={() => toggleSvc(s)} />
                      <button data-testid={`edit-service-${s.id}`} onClick={() => openSvc("edit", cat.id, s)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-service-${s.id}`} onClick={() => delSvc(s.id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!catDlg} onOpenChange={(o) => !o && setCatDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{catDlg?.mode === "new" ? "Neue Kategorie" : "Kategorie bearbeiten"}</DialogTitle>
            <DialogDescription>Kategorie für Dienstleistungen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input data-testid="category-name-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3"><span className="text-sm text-stone-600">Aktiv</span><Switch data-testid="category-active-switch" checked={active} onCheckedChange={setActive} /></div>
          </div>
          <DialogFooter><Button data-testid="save-category-button" onClick={saveCat} disabled={busy} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!svcDlg} onOpenChange={(o) => !o && setSvcDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{svcDlg?.mode === "new" ? "Neue Dienstleistung" : "Dienstleistung bearbeiten"}</DialogTitle>
            <DialogDescription>Dienstleistung in dieser Kategorie.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input data-testid="service-name-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3"><span className="text-sm text-stone-600">Aktiv</span><Switch data-testid="service-active-switch" checked={active} onCheckedChange={setActive} /></div>
          </div>
          <DialogFooter><Button data-testid="save-service-button" onClick={saveSvc} disabled={busy} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
